import type {
  AnswerValue,
  ContactPayload,
  GiftPayload,
  JourneyEventName,
  SafeEventMetadata,
  SessionState,
  Variant,
} from "./types";
import { PRODUCT, SURVEY_VERSION } from "./survey";

const SESSION_KEY_PREFIX = "swmb_warranty_session_";
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

type ApiError = Error & { status?: number };

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init.headers ?? {}),
    },
  });
  if (!response.ok) {
    const error = new Error((await response.json().catch(() => null))?.error ?? "Request failed") as ApiError;
    error.status = response.status;
    throw error;
  }
  return response.json() as Promise<T>;
}

export function storedToken(variant: Variant): { sessionId: string; accessToken: string } | null {
  try {
    const saved = localStorage.getItem(`${SESSION_KEY_PREFIX}${variant}`);
    if (!saved) return null;
    const value = JSON.parse(saved) as { sessionId?: string; accessToken?: string };
    return value.sessionId && value.accessToken ? { sessionId: value.sessionId, accessToken: value.accessToken } : null;
  } catch {
    return null;
  }
}

export function rememberToken(variant: Variant, session: SessionState): void {
  localStorage.setItem(
    `${SESSION_KEY_PREFIX}${variant}`,
    JSON.stringify({ sessionId: session.sessionId, accessToken: session.accessToken }),
  );
}

export async function openSession(variant: Variant): Promise<SessionState> {
  const existing = storedToken(variant);
  return request<SessionState>("/api/sessions", {
    method: "POST",
    body: JSON.stringify({
      product: PRODUCT,
      variant,
      surveyVersion: SURVEY_VERSION,
      resumeSessionId: existing?.sessionId,
      resumeToken: existing?.accessToken,
    }),
  });
}

function authHeaders(session: SessionState): HeadersInit {
  return { authorization: `Bearer ${session.accessToken}` };
}

export async function recordEvent(
  session: SessionState,
  eventName: JourneyEventName,
  details: {
    stepIndex?: number;
    questionId?: string;
    metadata?: SafeEventMetadata;
  } = {},
): Promise<void> {
  await request(`/api/sessions/${session.sessionId}/events`, {
    method: "POST",
    headers: authHeaders(session),
    body: JSON.stringify({
      eventId: crypto.randomUUID(),
      eventName,
      ...details,
      occurredAt: new Date().toISOString(),
    }),
    keepalive: eventName === "page_hidden",
  });
}

export async function saveAnswer(
  session: SessionState,
  questionId: string,
  answer: AnswerValue | null,
  skipped: boolean,
  stepIndex: number,
): Promise<{ savedAt: string }> {
  return request(`/api/sessions/${session.sessionId}/answers/${encodeURIComponent(questionId)}`, {
    method: "PUT",
    headers: authHeaders(session),
    body: JSON.stringify({ answer, skipped, stepIndex, eventId: crypto.randomUUID() }),
  });
}

export async function saveProgress(session: SessionState, stepIndex: number): Promise<void> {
  await request(`/api/sessions/${session.sessionId}/progress`, {
    method: "PUT",
    headers: authHeaders(session),
    body: JSON.stringify({ stepIndex }),
  });
}

export async function saveContact(session: SessionState, contact: ContactPayload): Promise<void> {
  await request(`/api/sessions/${session.sessionId}/contact`, {
    method: "PUT",
    headers: authHeaders(session),
    body: JSON.stringify(contact),
  });
}

export async function submitGiftClaim(session: SessionState, gift: GiftPayload): Promise<{ status: string }> {
  return request(`/api/sessions/${session.sessionId}/gift-claim`, {
    method: "POST",
    headers: authHeaders(session),
    body: JSON.stringify(gift),
  });
}

export async function completeSession(session: SessionState, screenedOut = false): Promise<void> {
  await request(`/api/sessions/${session.sessionId}/complete`, {
    method: "POST",
    headers: authHeaders(session),
    body: JSON.stringify({ screenedOut }),
  });
}

export async function adminRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const localKey = sessionStorage.getItem("swmb_local_admin_key") ?? "";
  return request<T>(`/api/admin${path}`, {
    ...init,
    headers: localKey ? { "x-local-admin-key": localKey } : undefined,
  });
}
